import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  usePrevious,
  useGetList,
  GeneralLedgerTransaction,
  ResponseError,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import { overviewAccountsSchema } from '../../individual/variables/overview-accounts';
import {
  useLazyGetOverviewStatementsListQuery,
  useLazyGetCustomersStatementPdfQuery,
} from '../store/services/customers';
import formatStatementsList from '../helpers/format-account-transactions';
import { TransactionTypeEnum, StatementListItemProps } from '../types';
import { UseStatementsTypes } from './types';

const useStatements = ({
  filters,
  onSortChange,
  onFilterChange,
  sort,
  statements,
  customerId,
  customerType,
  accountsPage,
  accountNumber,
}: UseStatementsTypes) => {
  const [accountId, setAccountId] = useState('');
  const [accountsStatements, setAccountsStatements] =
    useState<StatementListItemProps[]>();
  const [downloadedId, setDownloadedId] = useState('');
  const [statementError, setStatementError] = useState('');

  const { search } = useLocation();

  const { customersTransactionTypes, currenciesList, activePermissions } =
    useAppSelector((state) => state.customersDictionaries);

  const prevTransactionsTypes = usePrevious(customersTransactionTypes);
  const prevFilters = usePrevious(filters);

  const [getStatementsList, { isFetching, isLoading }] =
    useLazyGetOverviewStatementsListQuery();
  const [
    getCustomersStatementPdf,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetCustomersStatementPdfQuery();

  const onDownloadStatement = async (id: string) => {
    setDownloadedId(id);
    setStatementError('');

    const baseQuery = `accountId=${accountId}&organizationId=${customerId}`;

    const response = (await getCustomersStatementPdf({
      id,
      query: baseQuery,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `Statement ${accountNumber} ${id}.pdf`;
      hiddenElement.click();
      setDownloadedId('');
    } else if (response.error) {
      setDownloadedId('');

      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setStatementError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setStatementError(response.error.data.message);
        } else {
          setStatementError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const { getListAction, onSort, onFilter, errorList } =
    useGetList<GeneralLedgerTransaction>({
      getList: getStatementsList,
      onSortChange,
      onFilterChange,
      schema: overviewAccountsSchema,
      itemId: customerId,
    });

  const formattedData = useMemo(() => {
    const data = formatStatementsList(
      accountsStatements as StatementListItemProps[],
      customerType,
      onDownloadStatement,
      accountsPage,
      search,
      customerId,
      currenciesList,
      downloadInProgress || downloadIsFetching,
      downloadedId,
      activePermissions?.includes(
        PermissionEnum.ACCOUNTS_STATEMENTS_EXPORT_ACTION,
      ),
    );

    return data;
  }, [
    accountId,
    filters,
    accountsStatements,
    filters,
    downloadInProgress,
    downloadIsFetching,
    downloadedId,
    activePermissions,
  ]);

  useEffect(() => {
    if (statements) {
      setAccountsStatements(
        statements.map((item) => ({
          ...item,
          typeId: customersTransactionTypes?.find(
            (type) => type.name === TransactionTypeEnum.GENERAL_LEDGER,
          )?.id,
        })),
      );
    }
  }, [statements]);

  useEffect(() => {
    if (
      accountId &&
      !search &&
      !accountsStatements &&
      customersTransactionTypes &&
      !prevTransactionsTypes
    ) {
      getListAction({ sort });
    }
  }, [
    getListAction,
    accountsStatements,
    search,
    customersTransactionTypes,
    prevTransactionsTypes,
  ]);

  useEffect(() => {
    if (
      accountId &&
      !accountsStatements &&
      search &&
      filters &&
      Object.keys(filters).length &&
      prevFilters &&
      customersTransactionTypes &&
      !prevTransactionsTypes
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [
    accountId,
    filters,
    getListAction,
    accountsStatements,
    sort,
    search,
    prevFilters,
    customersTransactionTypes,
    prevTransactionsTypes,
  ]);

  return {
    accountId,
    isLoading,
    setAccountId,
    isFetching,
    onSort,
    onFilter,
    formattedData,
    errorList,
    statementError,
  };
};

export default useStatements;
